import { isEmpty, isNil } from 'es-toolkit/compat';
import { IMAGE_FILE_TYPE } from 'models/enums';
import { uploadDocumentToS3 } from '../common/uploadDocumentToS3';
import { uploadFiles } from '../images/actions';
import { getApiActionTypesValues } from '../redux_utils';
import { HOMEOWNER_SET_UP_FLOW } from './constants';

export const fetchStepData = (flowId, mappedStep) => ({
  types: getApiActionTypesValues(HOMEOWNER_SET_UP_FLOW.FETCH_STEP_DATA),
  promise: ({ client }) =>
    client.get(
      `/homeowners/set-up/${flowId}/${mappedStep?.group?.key}/${mappedStep?.key}/${mappedStep?.uniqueId}`,
    ),
  auth: true,
});

export const updateStepData =
  (flowId, mappedStep, stepFormData) => (dispatch) =>
    dispatch({
      types: getApiActionTypesValues(HOMEOWNER_SET_UP_FLOW.UPDATE_STEP_DATA),
      promise: ({ client }) =>
        client.put(
          `/homeowners/set-up/${flowId}/${mappedStep.group.key}/${mappedStep.key}/${mappedStep.uniqueId}`,
          {
            ...stepFormData,
          },
        ),
      auth: true,
    });

export const fetchAvailabilityDates = (homeId, startDate, endDate) => ({
  types: getApiActionTypesValues(
    HOMEOWNER_SET_UP_FLOW.FETCH_AVAILABILITY_DATES,
  ),
  promise: ({ client }) =>
    client.get(`/inspections/onboarding/availability-dates`, {
      params: {
        homeId,
        startDate,
        endDate,
      },
    }),
  auth: true,
});

const getBannerImageUploadUrl = (flowId, stepId) => ({
  promise: ({ client }) =>
    client.get(
      `/homeowners/set-up/${flowId}/unit/features/banner-url/${stepId}`,
    ),
  auth: true,
});

export const uploadBannerImage =
  (media, flowId, stepId) => async (dispatch) => {
    if (!media) return {};
    const { availableMedia, stagingMedia, deletedMedia, later } = media;
    const uploadUrlObject = await dispatch(
      getBannerImageUploadUrl(flowId, stepId),
    );
    const { uniqueId, headers, uploadUrl } = uploadUrlObject;
    if (isEmpty(stagingMedia) && later) return {};
    if (isNil(stagingMedia) && isNil(deletedMedia)) return availableMedia[0];
    await uploadDocumentToS3(dispatch, stagingMedia[0], headers, uploadUrl);
    return { mediaId: uniqueId, name: stagingMedia[0].name };
  };

export const updateDisclosuresFilesIfNecessary =
  (values) => async (dispatch) => {
    const newValues = structuredClone(values);

    if (
      !newValues?.disclosures?.types ||
      typeof newValues.disclosures.types !== 'object'
    ) {
      return newValues ?? {};
    }

    for (const type of Object.keys(newValues.disclosures.types)) {
      // If the disclosure has related files, upload them and return the result
      // so we can have the file data in the disclosures object
      if (newValues.disclosures.types[type].files?.stagingMedia?.length) {
        const uploadedFiles = await dispatch(
          uploadFiles(
            IMAGE_FILE_TYPE.HomeDisclosuresAttachment,
            newValues.disclosures.types[type].files,
          ),
        );
        delete newValues.disclosures.types[type].files;
        newValues.disclosures.types[type].documentsToUpsert = uploadedFiles;
      }
    }

    // DC housing-code violations attach a notice file per row. Upload each row's
    // media, persist the resulting fileUniqueId on the row, and collect every
    // uploaded file into the disclosure's documentsToUpsert payload.
    const housingCode = newValues.disclosures.types.HousingCodeViolations;
    if (housingCode && Array.isArray(housingCode.housingCodeViolations)) {
      const rowDocuments = [];

      for (const row of housingCode.housingCodeViolations) {
        if (!row) {
          continue;
        }

        if (
          row.file?.stagingMedia?.length ||
          row.file?.availableMedia?.length
        ) {
          const uploaded = await dispatch(
            uploadFiles(IMAGE_FILE_TYPE.HomeDisclosuresAttachment, row.file),
          );

          const uploadedFile = uploaded?.[0];

          if (uploadedFile?.uniqueId) {
            row.fileUniqueId = uploadedFile.uniqueId;
            rowDocuments.push(...uploaded);
          }
        }

        delete row.file;
      }

      if (rowDocuments.length) {
        housingCode.documentsToUpsert = [
          ...(housingCode.documentsToUpsert || []),
          ...rowDocuments,
        ];
      }
    }

    return newValues;
  };
